import { datadogRum } from '@datadog/browser-rum';
import jwtDecode from 'jwt-decode';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '.';

interface EcomUser {
  pharmacyId: string | null;
  groups: string[];
  username: string;
}

interface UserContextType {
  user: EcomUser | null;
  switchPharmacy: (pharmacyId: string) => void;
  clearPharmacy: () => void;
  hasSwitchedPharmacy: boolean;
}

const UserContext = createContext<UserContextType>({
  user: null,
  switchPharmacy: () => {},
  clearPharmacy: () => {},
  hasSwitchedPharmacy: false,
});
export const UserProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { accessToken } = useAuthContext();
  const [overWrittenPharmacyId, setOverWrittenPharmacyId] = useState<string | null>(null);
  if (!accessToken) {
    throw new Error('No access token available');
  }

  const contextValue = useMemo(() => {
    const decodedToken = jwtDecode<{ 'cognito:groups': string[]; username: string }>(accessToken);
    const pharmacyIdFromToken = /^\d+$/.test(decodedToken.username) ? decodedToken.username : null;
    return {
      user: {
        groups: decodedToken['cognito:groups'],
        username: decodedToken.username,
        pharmacyId: overWrittenPharmacyId || pharmacyIdFromToken || null,
      },
      switchPharmacy: setOverWrittenPharmacyId,
      clearPharmacy: () => setOverWrittenPharmacyId(null),
      hasSwitchedPharmacy: Boolean(overWrittenPharmacyId),
    };
  }, [accessToken, overWrittenPharmacyId]);

  useEffect(() => {
    datadogRum.setUser({
      id: contextValue.user.username,
    });
  }, [contextValue.user.username]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export const useCurrentUser = () => {
  const { logout } = useAuthContext();
  const { user, switchPharmacy, clearPharmacy, hasSwitchedPharmacy } = useContext(UserContext);

  if (!user) {
    throw new Error('No user available');
  }

  const isSuperAdmin = !hasSwitchedPharmacy && user.groups.includes('superadmin');
  const isAdmin = !hasSwitchedPharmacy && (isSuperAdmin || user.groups.includes('admin'));
  const isPharmacy = Boolean(user.pharmacyId);
  const isCustomerService = !hasSwitchedPharmacy && user.groups.includes('customer-service');
  const isWarehouse = !hasSwitchedPharmacy && user.groups.includes('warehouse');

  return {
    isAdmin,
    isSuperAdmin,
    pharmacyId: user.pharmacyId,
    username: user.username,
    hasSwitchedPharmacy,
    switchPharmacy,
    clearPharmacy,
    isPharmacy,
    isCustomerService,
    isWarehouse,
    logout,
  };
};
